import { useEffect, useState } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import UserAvatar from "../../components/common/user-avatar/UserAvatar";

import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { CustomButton, CustomTextBox } from "../../components";
import { PosLayout } from "../../components/layouts/pos-layout/PosLayout";
import { Colors } from "../../configs";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { loginAction } from "../../redux/actions/authAction";
import { useTranslation } from "../../util/translation-provider";
import { useNavigate } from "react-router-dom";
import { getTenantId } from "../../util/common-functions";
import { getRecentLogins, RecentLoginUser } from "../../util/recent-logins";
import TrayAppDownloadModal from "../../components/atoms/tray-app-download-modal/TrayAppDownloadModal";
import { useTrayAppAvailability } from "../../hooks/useTrayAppAvailability";
import { primaryAlpha } from "../../configs/colors";

type LoginFormInputs = {
  email: string;
  password: string;
};

const LoginPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tenantId = getTenantId();
  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<LoginFormInputs>();
  const { loading, loginResponse } = useAppSelector((state) => state.auth);
  const translation = useTranslation();
  const [formData, setFormData] = useState<LoginFormInputs | null>(null);
  const [recentLogins, setRecentLogins] = useState<RecentLoginUser[]>(() => getRecentLogins());
  const { checking: trayChecking, available: trayAvailable, downloadUrl: trayDownloadUrl, version: trayVersion } =
    useTrayAppAvailability(true);
  const [trayModalDismissed, setTrayModalDismissed] = useState(false);

  const onSubmit: SubmitHandler<LoginFormInputs> = (data) => {
    setFormData(data);
    dispatch(loginAction({ ...data, whiteLabel: tenantId }));
  };

  useEffect(() => {
    if (loginResponse?.status === "first-time") {
      navigate("/auth/password/change", { state: { email: formData?.email } });
    }
    if (loginResponse?.status === "success") {
      setRecentLogins(getRecentLogins());
    }
  }, [loginResponse]);

  const renderRecentLogins = () => {
    if (recentLogins.length === 0) {
      return (
        <Grid container spacing={2} sx={{ pt: "1.5rem" }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid
              item
              xs={4}
              key={index}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    border: "2px dashed #D1D5DB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#F9FAFB",
                  }}
                >
                  <PersonOutlineIcon sx={{ fontSize: "2rem", color: "#D1D5DB" }} />
                </Box>
                <Box
                  sx={{
                    mt: "0.5rem",
                    width: 48,
                    height: 10,
                    borderRadius: 4,
                    backgroundColor: "#E5E7EB",
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      );
    }

    const colSize = Math.min(recentLogins.length, 3);
    return (
      <Grid container spacing={2} sx={{ pt: "1.5rem" }}>
        {recentLogins.map((user: RecentLoginUser, index: number) => (
          <Grid
            item
            xs={12 / colSize}
            key={index}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Box
              onClick={() => setValue("email", user.email)}
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                "&:hover .avatar-ring": {
                  borderColor: Colors.PRIMARY,
                },
              }}
            >
              <UserAvatar
                name={user.name}
                id={index}
                size={72}
                onClick={() => setValue("email", user.email)}
              />
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  mt: "0.5rem",
                  color: Colors.TEXT_GRAY_DARK,
                }}
              >
                {user.name}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PosLayout>
      <>
        <TrayAppDownloadModal
          open={!trayChecking && !trayAvailable && !trayModalDismissed}
          onClose={() => setTrayModalDismissed(true)}
          downloadUrl={trayDownloadUrl}
          version={trayVersion}
          loading={trayChecking}
          blocking={false}
        />
        <Grid display="flex" sx={{ height: "100%" }}>
        {/* Left — logo / brand panel */}
        <Grid
          flex={7}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            py: "2.5rem",
            px: "3rem",
          }}
        >
          {/* Logo area */}
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <Box sx={{ maxWidth: "28rem", width: "100%", display: "flex", justifyContent: "center" }}>
              <img
                src={translation.loginLogo}
                alt={translation.companyName}
                style={{ width: "100%", maxWidth: "28rem", objectFit: "contain" }}
              />
            </Box>
          </Box>

          {/* Licenses & Certifications */}
          {translation.certificates && translation.certificates.length > 0 && (
            <Box sx={{ width: "100%", maxWidth: "32rem" }}>
              <Box sx={{ mb: "0.75rem", textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.18em",
                    color: Colors.PRIMARY,
                    lineHeight: 1.4,
                  }}
                >
                  Recognized By
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: Colors.TEXT_GRAY_DARK,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Licenses & Certifications
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: Colors.WHITE,
                  borderRadius: "1.5rem",
                  boxShadow: "0 8px 28px rgba(0,0,0,0.06)",
                  px: "1.75rem",
                  py: "1.25rem",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "1rem",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {translation.certificates.map((cert, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: "4.5rem",
                      height: "4.5rem",
                      borderRadius: "0.75rem",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <img
                      src={cert.src}
                      alt={cert.alt}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Grid>

        {/* Right — modal cards panel */}
        <Grid
          flex={5}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            height: "100%",
            overflowY: "auto",
            py: "1rem",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          <Box sx={{ width: "35rem" }}>
            {/* ── Login card ── */}
            <Box
              sx={{
                backgroundColor: Colors.WHITE,
                borderRadius: "2rem",
                boxShadow: "0 20px 56px rgba(0,0,0,0.10)",
                overflow: "visible",
                pb: "2rem",
              }}
            >
              {/* Brand avatar at top */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: "2.25rem",
                  pb: "0.75rem",
                }}
              >
                <Box
                  sx={{
                    width: "5rem",
                    height: "5rem",
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${Colors.PRIMARY}, ${Colors.PRIMARY_DARK})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 10px 28px ${primaryAlpha(0.40)}`,
                    border: "4px solid #fff",
                  }}
                >
                  <PersonOutlineIcon sx={{ color: "#fff", fontSize: "2.6rem" }} />
                </Box>
              </Box>

              {/* Heading */}
              <Box sx={{ textAlign: "center", mb: "1.5rem" }}>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.18em",
                    color: Colors.PRIMARY,
                    lineHeight: 1.4,
                  }}
                >
                  Sign In
                </Typography>
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 700,
                    color: Colors.TEXT_GRAY_DARK,
                    lineHeight: "2.4rem",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {translation.companyName}
                </Typography>
              </Box>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Box sx={{ px: "2.5rem" }}>
                  <Box sx={{ mb: "1.1rem" }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        color: Colors.TEXT_GRAY_DARK,
                        mb: "0.4rem",
                      }}
                    >
                      Username
                    </Typography>
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: { message: "Username is required", value: true },
                        pattern: {
                          message: "Please insert valid email",
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <CustomTextBox
                          {...field}
                          error={fieldState.invalid}
                          sx={{
                            width: "100%",
                            backgroundColor: Colors.BACKGROUND_GRAY,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0.875rem",
                              "& fieldset": { border: "1.5px solid #E5E7EB" },
                              "&:hover fieldset": {
                                borderColor: Colors.PRIMARY,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: Colors.PRIMARY,
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ mb: "1.5rem" }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        color: Colors.TEXT_GRAY_DARK,
                        mb: "0.4rem",
                      }}
                    >
                      Password
                    </Typography>
                    <Controller
                      name="password"
                      control={control}
                      rules={{
                        required: { message: "Password is required", value: true },
                      }}
                      render={({ field, fieldState }) => (
                        <CustomTextBox
                          {...field}
                          error={fieldState.invalid}
                          type="password"
                          sx={{
                            width: "100%",
                            backgroundColor: Colors.BACKGROUND_GRAY,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0.875rem",
                              "& fieldset": { border: "1.5px solid #E5E7EB" },
                              "&:hover fieldset": {
                                borderColor: Colors.PRIMARY,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: Colors.PRIMARY,
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  <CustomButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      width: "100%",
                      height: "3.2rem",
                      borderRadius: "1.2rem",
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      boxShadow: `0 8px 20px ${primaryAlpha(0.28)}`,
                      transition: "transform 0.15s ease, box-shadow 0.15s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 12px 28px ${primaryAlpha(0.36)}`,
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      "Login"
                    )}
                  </CustomButton>

                  {loginResponse?.status &&
                    loginResponse.status !== "success" &&
                    loginResponse.status !== "first-time" && (
                    <Typography
                      sx={{
                        mt: "0.85rem",
                        fontSize: "0.875rem",
                        color: Colors.ERROR_RED,
                        textAlign: "center",
                      }}
                    >
                      {loginResponse?.message}
                    </Typography>
                  )}

                  <Typography
                    sx={{
                      mt: "1.1rem",
                      fontSize: "0.8rem",
                      color: Colors.ACCENT_GRAY_DISABLED,
                      textAlign: "center",
                      lineHeight: 1.5,
                    }}
                  >
                    Forgot password? Contact your manager.{" "}
                  </Typography>
                </Box>
              </form>
            </Box>

            {/* ── Recent Logins card ── */}
            <Box
              sx={{
                backgroundColor: Colors.BACKGROUND_GRAY,
                borderRadius: "2rem",
                boxShadow: "0 8px 28px rgba(0,0,0,0.06)",
                mt: "1.25rem",
                px: "2rem",
                pt: "1.75rem",
                pb: "2rem",
              }}
            >
              <Box sx={{ mb: "0.25rem" }}>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.18em",
                    color: Colors.SECONDARY,
                    lineHeight: 1.4,
                  }}
                >
                  Quick Access
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: Colors.TEXT_GRAY_DARK,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Recent Logins
                </Typography>
              </Box>
              {renderRecentLogins()}
            </Box>
          </Box>
        </Grid>
        </Grid>
      </>
    </PosLayout>
  );
};

export { LoginPage };
